import { extend } from 'acorn-jsx-walk'
import { ancestor, base } from 'acorn-walk'

import { replaceNode, t } from './utils'

import type { AnyNode } from 'acorn'

extend(base)

export function transformJsx<T extends AnyNode>(node: T): T

export function transformJsx(node: AnyNode): AnyNode {
  ancestor(node, {
    JSXElement(node, state, ancestors) {
      const props = node.openingElement.attributes.map((attr) => {
        if (t.isJSXSpreadAttribute(attr)) return t.spreadElement(attr.argument)
        return t.property(
          transformJSXPropertyName(attr.name),
          attr.value ? transformJSXAttributeValue(attr.value) : t.literal(true)
        )
      })
      replaceNode(
        ancestors,
        t.callExpression(
          t.memberExpression(
            t.identifier('React'),
            t.identifier('createElement')
          ),
          [
            transformJSXName(node.openingElement.name),
            props.length > 0 ? t.objectExpression(props) : t.literal(null),
            ...transformJSXChildren(node.children)
          ]
        )
      )
    },
    JSXFragment(node, state, ancestors) {
      replaceNode(
        ancestors,
        t.callExpression(
          t.memberExpression(
            t.identifier('React'),
            t.identifier('createElement')
          ),
          [
            t.memberExpression(t.identifier('React'), t.identifier('Fragment')),
            t.literal(null),
            ...transformJSXChildren(node.children)
          ]
        )
      )
    }
  })
  return node
}

function transformJSXAttributeValue(node: AnyNode): AnyNode {
  switch (node.type) {
    case 'JSXElement': {
      return transformJsx(node)
    }
    case 'JSXExpressionContainer': {
      return t.isJSXEmptyExpression(node.expression)
        ? t.literal(true)
        : node.expression
    }
    case 'JSXFragment': {
      return transformJsx(node)
    }
    case 'Literal': {
      return t.literal(node.value)
    }
    default: {
      return node
    }
  }
}

function transformJSXChildren(children: AnyNode[]): AnyNode[] {
  return children
    .map((child) => {
      switch (child.type) {
        case 'JSXElement': {
          return transformJsx(child)
        }
        case 'JSXExpressionContainer': {
          return t.isJSXEmptyExpression(child.expression)
            ? null
            : child.expression
        }
        case 'JSXFragment': {
          return transformJsx(child)
        }
        case 'JSXSpreadChild': {
          return t.spreadElement(child.expression)
        }
        case 'JSXText': {
          const text = child.value.replaceAll(/\s+/g, ' ')
          return text.trim() ? t.literal(text.trim()) : null
        }
        default: {
          return child
        }
      }
    })
    .filter(Boolean)
}

function transformJSXName(node: AnyNode): AnyNode {
  switch (node.type) {
    case 'JSXIdentifier': {
      return /^[A-Z]/.test(node.name)
        ? t.identifier(node.name)
        : t.literal(node.name)
    }
    case 'JSXMemberExpression': {
      return t.memberExpression(
        transformJSXName(node.object),
        transformJSXName(node.property)
      )
    }
    case 'JSXNamespacedName': {
      return t.literal(`${node.namespace.name}:${node.name.name}`)
    }
    default: {
      return node
    }
  }
}
function transformJSXPropertyName(node: AnyNode): AnyNode {
  const jsxName = transformJSXName(node)
  return t.isLiteral(jsxName) &&
    /^[$A-Z_a-z][\w$]*$/.test(String(jsxName.value))
    ? t.identifier(jsxName.value)
    : jsxName
}
